import axios from 'axios';
import { User, LoginResponse, CreateUserRequest } from '../types';
import { getApiUrl, loadAppConfig } from '../config/appConfig';

const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Actualizar la baseURL cuando la configuración esté disponible
// La configuración se carga en index.tsx antes de renderizar la app
loadAppConfig().then(() => {
  api.defaults.baseURL = getApiUrl();
  console.log('🔧 API Base URL configurada:', api.defaults.baseURL);
});

// Configuración para aceptar certificados autofirmados en desarrollo
if (process.env.NODE_ENV === 'development') {
  process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
}

// Inicializar token desde localStorage si existe
const initialToken = localStorage.getItem('token');
if (initialToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
}

// Interceptor para ver las peticiones y agregar token automáticamente
api.interceptors.request.use(
  (config) => {
    // Agregar token desde localStorage si existe (por si acaso no está en los defaults)
    const token = localStorage.getItem('token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log('%c 🚀 Petición saliente:', 'background: #222; color: #bada55', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data,
      params: config.params
    });
    return config;
  },
  (error) => {
    console.error('%c ❌ Error en la petición:', 'background: #222; color: #ff0000', error);
    return Promise.reject(error);
  }
);

// Interceptor para ver las respuestas
api.interceptors.response.use(
  (response) => {
    console.log('%c ✅ Respuesta recibida:', 'background: #222; color: #00ff00', {
      status: response.status,
      headers: response.headers,
      data: response.data
    });
    return response;
  },
  async (error) => {
    if (error.response) {
      console.error('%c ❌ Error de respuesta:', 'background: #222; color: #ff0000', {
        status: error.response.status,
        headers: error.response.headers,
        data: error.response.data
      });
    } else if (error.request) {
      console.error('%c ❌ Error de petición:', 'background: #222; color: #ff0000', error.request);
    } else {
      console.error('%c ❌ Error:', 'background: #222; color: #ff0000', error.message);
    }

    // Manejo del error de autenticación
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No hay refresh token');
        }

        const response = await api.post<LoginResponse>('/api/Auth/RefreshToken', { refreshToken });
        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken || refreshToken);

        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        // Limpiar selección de grupo de caja si la sesión expira/forzamos login
        localStorage.removeItem('grupoCajaChicaSeleccionado');
        localStorage.removeItem('grupoCajaMcp');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    if (!navigator.onLine) {
      // Manejar el estado offline
      console.log('No hay conexión a internet');
      // Aquí puedes mostrar un mensaje al usuario
    }

    return Promise.reject(error);
  }
);

export const userService = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/api/auth/login', { username, password });
    return response.data;
  },

  getUsers: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/api/users');
    return response.data;
  },

  createUser: async (user: CreateUserRequest): Promise<User> => {
    const response = await api.post<User>('/api/users', {
      ...user,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  },

  updateUser: async (id: number, user: Partial<User>): Promise<User> => {
    const response = await api.put<User>(`/api/users/${id}`, {
      ...user,
      updatedAt: new Date().toISOString(),
    });
    return response.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/api/users/${id}`);
  },
};

export default api; 